import React, {useEffect, useState} from "react";
import {FormikProps, useFormik} from "formik";
import * as Yup from "yup";
import {FormInput, Forms, GradientButton, notify, QrCode} from "@housebets/housebets-x-package";

import {ITwoFactorAuthFormProps, TFormikConfig, TPayload} from "./types";
import {useAppSelector} from "hooks/useAppSelector";
import apiUser from "app/api/user";
import {toast} from "react-toastify";
import {useIntl, FormattedMessage} from "react-intl";

import {setOtherInfoUser} from "app/store/slices/user";
import { useDispatch } from "react-redux";

const initData: TFormikConfig = {
    secretKey: "",
    code: "",
};

const initForm = (formik: FormikProps<TFormikConfig>, setError?: (v: boolean) => void) => {
    formik.setValues({...initData});
    formik.setErrors({});
    formik.setTouched({});
    setError?.(false);
};

export const TwoFactorAuthForm: React.FC<ITwoFactorAuthFormProps> = ({setIsVerified, verified, onSubmit = () => {}}) => {
    const [isLoading, setIsLoading] = useState(false);
    const userData = useAppSelector((state) => state.user.user);
    const {$t} = useIntl();
    const dispatch = useDispatch();

    const formSchema = () =>
        Yup.object().shape({
            secretKey: Yup.string().nullable(),
            code: Yup.string().required($t({id: "FieldIsRequired"})),
        });

    const [error, setError] = useState("");
    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: {...initData},
        validationSchema: formSchema(),
        validateOnBlur: true,
        onSubmit: async (data) => {
            const {code} = data;

            try {
                setIsLoading(true);
                const userToken = userData?.token || "";
                const userId = userData?.userId || "";

                const res = verified === false ? await apiUser.googleVerification(userToken, code) : await apiUser.closeGoogleVerification(code);

                if (res.data.success) {
                    if (verified === false) {
                        notify({type: "success", title: $t({id: "TwoFactorAuthForm.twoFactorEnabled"})});
                        setIsVerified(true);
                    } else {
                        notify({type: "success", title: $t({id: "TwoFactorAuthForm.twoFactorDisabled"})});
                        setIsVerified(false);
                    }

                    initForm(formik);
                    onSubmit();

                    if (userId) {
                        const resDataUser = await apiUser.getOtherUserData(userId);
                        if (resDataUser.status === 200 && resDataUser.data.code === 0) {
                            // setData(res.data.data);
                            dispatch(setOtherInfoUser(resDataUser.data.data));
                        }
                    }
                } else {
                    setError("Invalid field");
                    notify({
                        type: "error",
                        title: $t({id: "TwoFactorAuthForm.activationFailed"}),
                    });
                    // toast.error($t({id: `GoogleVerification_Error_${res.data.code}`}));
                }
            } catch (er) {
                setError("Invalid field");
                notify({
                    type: "error",
                    title: $t({id: "TwoFactorAuthForm.activationFailed"}),
                });
            } finally {
                setIsLoading(false);
            }
        },
    });

    const [googleVerificationQrCode, setGoogleVerificationQrCode] = useState("");

    const getGoogleVerificationQrCode = async () => {
        if (userData?.token) {
            const {status, data} = await apiUser.getGoogleVerificationQrCode(userData?.token);
            if (status === 200 && data.code === 0) {
                setGoogleVerificationQrCode(data.data);
            }
        }
    };

    useEffect(() => {
        initForm(formik);
    }, []);

    useEffect(() => {
        if (userData) {
            getGoogleVerificationQrCode();
        }
    }, [userData]);

    const handleRemoveFa2 = () => {
        const res = apiUser.closeGoogleVerification(formik.values.code);
    };

    return (
        <Forms.TwoFactorAuth.Wrapper>
            <Forms.TwoFactorAuth.Form onSubmit={formik.handleSubmit}>
                <Forms.TwoFactorAuth.Row isHeder>
                    <div>{verified === false ? $t({id: "TwoFactorAuthForm.enableText"}) : $t({id: "TwoFactorAuthForm.disableText"})}</div>
                </Forms.TwoFactorAuth.Row>
                {verified === false ? (
                    <Forms.TwoFactorAuth.Row>
                        {googleVerificationQrCode ? <QrCode svgType={false} img={`data:image/png;base64,${googleVerificationQrCode}`} v2 /> : null}
                    </Forms.TwoFactorAuth.Row>
                ) : null}
                {verified === false ? (
                    <Forms.TwoFactorAuth.Row>
                        <FormInput
                            showIcon={false}
                            size="sm"
                            placeholder="Enter your secret key"
                            name="secretKey"
                            // type="text"
                            type="password"
                            label="Your secret key"
                            value={userData?.googleAutoCrypto}
                            disabled={true}
                        />
                        <p>{$t({id: "TwoFactorAuthForm.secretKeyHelp"})}</p>
                    </Forms.TwoFactorAuth.Row>
                ) : null}
                <Forms.TwoFactorAuth.Row>
                    {/* <div onClick={handleRemoveFa2}>remove fa2</div> */}
                    <FormInput
                        showIcon={false}
                        size="sm"
                        placeholder={$t({id: "TwoFactorAuthForm.codePlaceholder"})}
                        disabled={isLoading}
                        name="code"
                        type="text"
                        label={$t({id: "TwoFactorAuthForm.codeLabel"})}
                        required
                        value={formik.values?.code}
                        errors={
                            (!!formik.errors?.code && !!formik.touched?.code) || error
                                ? {
                                      message: formik.errors?.code || error,
                                  }
                                : null
                        }
                        onChange={(val) => formik.setFieldValue("code", val)}
                        onFocus={() => formik.setFieldTouched("code", false)}
                        onBlur={() => formik.setFieldTouched("code", true)}
                    />
                </Forms.TwoFactorAuth.Row>
                <Forms.TwoFactorAuth.Row>
                    <GradientButton disabled={isLoading} type="submit" text={$t({id: "TwoFactorAuthForm.verifyButton"})} kind="fill" fullWidth />
                </Forms.TwoFactorAuth.Row>
            </Forms.TwoFactorAuth.Form>
        </Forms.TwoFactorAuth.Wrapper>
    );
};

TwoFactorAuthForm.displayName = "TwoFactorAuthForm";
